import React, { useState } from 'react'
import './styles/component-styles.scss'
import { Copy, EyeSlash, Pencil, Trash, Eye, CurrencyCircleDollar, Users } from '@phosphor-icons/react'
import { TicketsResp } from 'types/event/eventStep4'
import { capitalizeFirstLetter } from 'helper/utility'
import { Dropdown } from 'antd'
import AdmissionTicket from '../Modals/AdmissionTickets/AdmissionTicket'
import { classesEventStore } from 'state/classes/classesStore'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { getAdmissionTicketPriceSentence, getAgeRangeWithGender } from '../../../../utility'
import { useEditStep6TicketStatusMutation, useDeleteClassAdmissionTicket, useDeleteClassAdditionalTicket, useDuplicateClassAdmissionTicket, useDuplicateClassAdditionalTicket } from 'api/Classes/Step6Tickets/Tickets'
import AlertModal from 'helper/AlertModal'
import AlertDuplication from 'helper/DuplicationAlert'

type TicketType = 'admission' | 'additional'

interface TicketsComp {
  visible: boolean
  details: TicketsResp
  ticketType: TicketType
}

const TicketComp: React.FC<TicketsComp> = ({ visible, details, ticketType }) => {
  const classesData = classesEventStore(store => store.classesData) as any
  const setClassesData = classesEventStore(store => store.setClassesData)

  const [openAdmissionTicket, setOpenAdmissionTicekt] = useState<boolean>(false)
  const [alertModalOpen, setAlertModalOpen] = useState<boolean>(false)
  const [alertDuplicationOpen, setAlertDuplicationOpen] = useState<boolean>(false)
  
  const { mutate: changeTicketStatus, isLoading: changeTicketStatusLoading } =
    useEditStep6TicketStatusMutation()
  
  const { mutate: deleteAdmissionTicket, isLoading: deleteAdmissionLoading } = 
    useDeleteClassAdmissionTicket()
  const { mutate: deleteAdditionalTicket, isLoading: deleteAdditionalLoading } = 
    useDeleteClassAdditionalTicket()
  const { mutate: duplicateAdmissionTicket, isLoading: duplicateAdmissionLoading } = 
    useDuplicateClassAdmissionTicket()
  const { mutate: duplicateAdditionalTicket, isLoading: duplicateAdditionalLoading } = 
    useDuplicateClassAdditionalTicket()

  const handleMenuClick = (operation: 'edit' | 'visibility' | 'delete' | 'duplicate') => {
    if (operation === 'edit') {
      setOpenAdmissionTicekt(true)
      return
    }

    if (operation === 'delete') {
      setAlertModalOpen(true)
      return
    }

    if (operation === 'duplicate') {
      setAlertDuplicationOpen(true)
      return
    }

    const payload = {
      status: visible ? 'disabled' : 'active',
      admissionTicketId: ticketType === 'admission' ? details._id : undefined,
      additionalOptionId: ticketType === 'additional' ? details._id : undefined,
    } as any

    changeTicketStatus(
      { classId: classesData?._id, body: payload },
      {
        onSuccess: newData => {
          setClassesData({ ...classesData, ...newData })
        },
      },
    )
  }

  const handleYesDelete = () => {
    if (ticketType === 'admission') {
      deleteAdmissionTicket(
        { classId: classesData?._id, ticketId: details._id },
        {
          onSuccess: newData => {
            setClassesData({ ...classesData, ...newData })
            setAlertModalOpen(false)
          },
        },
      )
    } else if (ticketType === 'additional') {
      deleteAdditionalTicket(
        { classId: classesData?._id, ticketId: details._id },
        {
          onSuccess: newData => {
            setClassesData({ ...classesData, ...newData })
            setAlertModalOpen(false)
          },
        },
      )
    }
  }

  const handleYesDuplicate = () => {
    if (ticketType === 'admission') {
      duplicateAdmissionTicket(
        { classId: classesData?._id, ticketId: details._id },
        {
          onSuccess: newData => {
            setClassesData({ ...classesData, ...newData })
            setAlertDuplicationOpen(false)
          },
        },
      )
    } else if (ticketType === 'additional') {
      duplicateAdditionalTicket(
        { classId: classesData?._id, ticketId: details._id },
        {
          onSuccess: newData => {
            setClassesData({ ...classesData, ...newData })
            setAlertDuplicationOpen(false)
          },
        },
      )
    }
  }

  const manageOptions = [
    {
      key: 'edit',
      label: (
        <div
          className="flex gap-16px align-center"
          onClick={() => handleMenuClick('edit')}
        >
          <Pencil size={20} color="#000" />
          <p className="text-14 w-500">Edit ticket</p>
        </div>
      ),
    },
    {
      key: 'duplicate',
      label: (
        <div
          className="flex gap-16px align-center"
          onClick={() => handleMenuClick('duplicate')}
        >
          <Copy size={20} color="#000" />
          <p className="text-14 w-500">Duplicate ticket</p>
        </div>
      ),
    },
    {
      key: 'visibility',
      label: (
        <div
          className="flex gap-16px align-center"
          onClick={() => handleMenuClick('visibility')}
        >
          {visible ? <EyeSlash size={20} color="#000" /> : <Eye size={20} color="#000" />}
          <p className="text-14 w-500">Make {visible ? 'Disable' : 'Enable'}</p>
        </div>
      ),
    },
    {
      key: 'delete',
      label: (
        <div
          className="flex gap-16px align-center"
          onClick={() => handleMenuClick('delete')}
        >
          <Trash size={20} color="#000" />
          <p className="text-14 w-500">Delete ticket</p>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="image-ticket-main-cont">
        <div className="ticket-main-details">
          <div className="img-container">
            {(details as TicketsResp).photo ? (
              <img
                src={(details as TicketsResp).photo.post}
                alt="ticket_photo"
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <div className="w-full h-full" style={{ background: `#${details.color}` }} />
            )}
          </div>
          <div>
            <h2 className="text-18 w-500">{capitalizeFirstLetter(details.name)}</h2>
            <div className="mt-5px flex align-center gap-10px radius-full bg-grey w-fit px-10px py-5px">
              <CurrencyCircleDollar size={24} color="#000" />
              <p>
                {getAdmissionTicketPriceSentence(
                  details.price,
                  (details as TicketsResp).numberOfDays,
                  (details as TicketsResp).days,
                )}
              </p>
            </div>
            <div className="mt-5px flex align-center gap-10px radius-full bg-grey w-fit px-10px py-5px">
              <Users size={24} color="#000" />
              <p>
                {getAgeRangeWithGender(
                  (details as TicketsResp).minAge as number,
                  (details as TicketsResp).maxAge as number,
                  (details as TicketsResp).guestAllowedToVisit,
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-10px justify-between align-end text-right">
            <h2 className="text-18 w-500">{`$${(details as TicketsResp).price.toFixed(2)}`}</h2>

            {changeTicketStatusLoading ? (
              <ButtonSpinner size={20} color={'#000'} />
            ) : visible ? (
              <Eye size={20} color="#000" />
            ) : (
              <EyeSlash size={20} color="#000" />
            )}

            <Dropdown
              menu={{ items: manageOptions }}
              placement="bottomLeft"
              arrow={false}
              className={`ml-20px mr-10px ${changeTicketStatusLoading && 'no-pointer-events'}`}
              overlayStyle={{ width: '300px' }}
              trigger={['click']}
            >
              <button className={`ticket-button pointer`}>Manage</button>
            </Dropdown>
          </div>
        </div>

        <p className="mt-10px">{(details as TicketsResp).description}</p>
      </div>

      {openAdmissionTicket && (
        <AdmissionTicket
          modalOpen={openAdmissionTicket}
          modalClose={setOpenAdmissionTicekt}
          type={ticketType}
          data={details}
        />
      )}

      {alertModalOpen && (
        <AlertModal
          isModalVisible={alertModalOpen}
          setIsModalVisible={setAlertModalOpen}
          data={{
            name: `${ticketType} ticket`,
            heading: `Delete ${ticketType} ticket`,
            message1: `Are you sure you want to delete this ${ticketType} ticket? This action cannot be undone.`
          }}
          onYesClick={handleYesDelete}
          loading={ticketType === 'admission' ? deleteAdmissionLoading : deleteAdditionalLoading}
        />
      )}

      {alertDuplicationOpen && (
        <AlertDuplication
          isModalVisible={alertDuplicationOpen}
          setIsModalVisible={setAlertDuplicationOpen}
          data={{
            name: `${ticketType} ticket`,
            heading: `Duplicate ${ticketType} ticket`,
            message1: `Are you sure you want to duplicate this ${ticketType} ticket?`
          }}
          onYesClick={handleYesDuplicate}
          loading={ticketType === 'admission' ? duplicateAdmissionLoading : duplicateAdditionalLoading}
        />
      )}
    </>
  )
}

export default TicketComp

